import { RRDate } from '@raskrask-dk/raskrask-utils'

interface IDurationObject {
    year?: number | undefined
    quarter?: number | undefined
    month?: number | undefined
    week?: number | undefined
    day?: number | undefined
    hour?: number | undefined
    minute?: number | undefined
    second?: number | undefined
    millisecond?: number | undefined
}

/**
 * Decorator for caching results of methods
 * @param when the result should be recalculated
 * @returns either cached result or calculated IF not expired
 */
function cache<T>(
    expiration: IDurationObject
): (target: any, propertyKey: string, descriptor: PropertyDescriptor) => void {
    const cacheStore: { [k: string]: { expires: RRDate; returned: T } } = {}

    return function (
        _target: any,
        _propertyKey: string,
        descriptor: PropertyDescriptor
    ): void {
        const sourceFn = descriptor.value

        descriptor.value = function (...args: any[]): T {
            const cacheKey = keyFromArgs(args)
            // eslint-disable-next-line no-prototype-builtins
            const hasBeenCached = cacheStore.hasOwnProperty(cacheKey)
            const now = new RRDate()
            const isExpired =
                hasBeenCached && now.compare(cacheStore[cacheKey].expires) < 0

            if (!hasBeenCached || isExpired) {
                cacheStore[cacheKey] = {
                    expires: now.plus(expiration),
                    returned: sourceFn.apply(this, args),
                }
            }

            return cacheStore[cacheKey].returned
        }
    }
}

/**
 * Generate a unique key from function arguments
 * @param args - the arguments to generate a key from
 * @returns a unique string for function arguments
 */
const keyFromArgs = (args: any[]): string => {
    return args.reduce((acc, curr) => {
        return acc + JSON.stringify(curr)
    }, '')
}

export { cache }
